import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";
import { UserPlus, Trash2, Users } from "lucide-react";

export default function Settings({ team, refetchAll }) {
  const [name, setName] = useState("");
  const [role, setRole] = useState("Sales Rep");
  const [saving, setSaving] = useState(false);

  const add = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error("Enter a name");
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/team`, { name: name.trim(), role });
      toast.success(`${name} added to the team`);
      setName("");
      setRole("Sales Rep");
      await refetchAll();
    } catch {
      toast.error("Failed to add member");
    } finally {
      setSaving(false);
    }
  };

  const changeRole = async (id, newRole) => {
    try {
      await axios.patch(`${API}/team/${id}`, { role: newRole });
      toast.success("Role updated");
      await refetchAll();
    } catch {
      toast.error("Failed to update role");
    }
  };

  const remove = async (id, memberName) => {
    try {
      await axios.delete(`${API}/team/${id}`);
      toast.success(`${memberName} removed`);
      await refetchAll();
    } catch {
      toast.error("Failed to remove");
    }
  };

  return (
    <div className="p-6 sm:p-8 max-w-4xl w-full mx-auto">
      <header className="mb-8">
        <div className="text-[11px] uppercase tracking-[0.14em] text-blue-600 font-semibold">
          Team roster
        </div>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 mt-1">
          Manage sales team members
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          Members added here become available in the Lead Library assignment dropdown.
        </p>
      </header>

      <form
        onSubmit={add}
        data-testid="team-add-form"
        className="bg-white rounded-xl border border-slate-200 shadow-sm p-5 flex flex-wrap items-end gap-3 mb-6"
      >
        <div className="flex-1 min-w-[200px]">
          <label className="text-[11px] uppercase tracking-[0.12em] text-slate-500 font-semibold mb-1.5 block">
            Full name *
          </label>
          <input
            data-testid="member-name-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Ahmar"
            className="w-full h-10 px-3 rounded-md border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
          />
        </div>
        <div className="min-w-[180px]">
          <label className="text-[11px] uppercase tracking-[0.12em] text-slate-500 font-semibold mb-1.5 block">
            Role
          </label>
          <select
            data-testid="member-role-input"
            value={role}
            onChange={(e) => setRole(e.target.value)}
            className="h-10 px-3 rounded-md border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30"
          >
            <option>Admin</option>
            <option>Sales Rep</option>
            <option>Lead Specialist</option>
          </select>
        </div>
        <button
          data-testid="add-member-btn"
          type="submit"
          disabled={saving}
          className="inline-flex items-center gap-2 h-10 px-4 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white text-sm font-semibold rounded-md"
          style={{ transition: "background-color 160ms ease" }}
        >
          <UserPlus size={16} />
          Add member
        </button>
      </form>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-2">
          <Users size={16} className="text-slate-500" />
          <h2 className="font-heading text-lg font-semibold text-slate-800 tracking-tight">
            Current team ({team.length})
          </h2>
        </div>
        <ul className="divide-y divide-slate-100">
          {team.length === 0 && (
            <li className="py-12 text-center text-sm text-slate-500">
              No team members yet. Add your first rep above.
            </li>
          )}
          {team.map((m, idx) => (
            <li
              key={m.id}
              data-testid={`team-row-${idx}`}
              className="px-5 py-4 flex items-center justify-between row-hover"
            >
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-sm font-bold">
                  {m.name.slice(0, 1).toUpperCase()}
                </div>
                <div>
                  <div className="font-semibold text-slate-900 text-sm">{m.name}</div>
                  <select
                    data-testid={`edit-role-${idx}`}
                    value={m.role}
                    onChange={(e) => changeRole(m.id, e.target.value)}
                    className="text-xs text-slate-500 bg-transparent border border-slate-200 rounded px-1.5 py-0.5 mt-0.5 focus:outline-none focus:ring-1 focus:ring-blue-500/30"
                  >
                    <option>Admin</option>
                    <option>Sales Rep</option>
                    <option>Lead Specialist</option>
                  </select>
                </div>
              </div>
              <button
                data-testid={`delete-member-${idx}`}
                onClick={() => remove(m.id, m.name)}
                className="text-slate-400 hover:text-rose-600 p-2"
                style={{ transition: "color 160ms ease" }}
                aria-label="Remove"
              >
                <Trash2 size={16} />
              </button>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
